import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Building2, Mail, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      toast.success('Login successful');
      navigate('/');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to login';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-split">
      {/* Left Panel - Hero Image */}
      <div className="login-hero relative">
        <div className="absolute inset-0 bg-slate-900 bg-opacity-70 flex items-center justify-center">
          <div className="text-center text-white p-8">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-orange-500" />
            <h1 className="text-4xl font-black mb-4 font-['Chivo'] tracking-tight">
              APS MODEL VIEWER
            </h1>
            <p className="text-xl text-slate-200 font-light">
              Navigate ACC/BIM 360 Projects & Generate Public 3D Model URLs
            </p>
          </div>
          {/* Subtle branding */}
          <div className="absolute bottom-6 left-0 right-0 text-center">
            <p className="text-xs text-slate-400 font-light">
              Built with clarity by{' '}
              <a 
                href="https://thedevguys.in" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-orange-400 transition-colors underline decoration-slate-500 hover:decoration-orange-400"
              >
                The Dev Guys
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="login-form-container bg-slate-50 flex items-center justify-center p-8">
        <div className="technical-form w-full max-w-md">
          <Card className="card border-slate-200">
            <CardHeader className="text-center pb-6">
              <CardTitle className="technical-header text-2xl mb-2">
                SIGN IN
              </CardTitle>
              <CardDescription className="technical-subtext">
                Sign in to your account to access your APS projects
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="form-group">
                  <Label htmlFor="email" className="form-label">
                    <Mail className="w-4 h-4 inline mr-2" />
                    EMAIL ADDRESS
                  </Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field"
                    placeholder="Enter your email address"
                    required
                  />
                </div>

                <div className="form-group">
                  <Label htmlFor="password" className="form-label">
                    <Lock className="w-4 h-4 inline mr-2" />
                    PASSWORD
                  </Label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field"
                    placeholder="Enter your password"
                    required
                  />
                </div>

                {error && (
                  <Alert variant="destructive" className="fade-in">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  data-testid="login-button"
                  disabled={isLoading || !email || !password}
                  className="btn-primary w-full h-12 text-sm font-semibold"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      SIGNING IN...
                    </>
                  ) : (
                    'SIGN IN'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="technical-subtext text-sm">
                  Don't have an account?{' '}
                  <Link 
                    to="/register" 
                    className="text-orange-500 hover:text-orange-600 font-medium transition-colors"
                    data-testid="register-link"
                  >
                    Create Account
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}